import React, { useState } from "react";
import { api, API } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Header } from "@/pages/Vitals";
import { Download, Link2, Copy, FileText, Calendar as CalIcon, Share2, Crown, Lock } from "lucide-react";

const fmt = (d) => d.toISOString().slice(0, 10);

export default function Reports() {
  const { members, activeId } = useMembers();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.tier === "pro";
  const today = new Date();
  const ninetyAgo = new Date(); ninetyAgo.setDate(today.getDate() - 90);
  const [memberId, setMemberId] = useState(activeId || "");
  const [start, setStart] = useState(fmt(ninetyAgo));
  const [end, setEnd] = useState(fmt(today));
  const [link, setLink] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  React.useEffect(() => { if (!memberId && activeId) setMemberId(activeId); }, [activeId, memberId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const url = `${API}/reports/pdf?start=${start}&end=${end}${memberId ? `&member_id=${memberId}` : ""}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Bad response");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `verda-health-${start}-to-${end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PDF downloaded");
    } catch { toast.error("Could not download"); }
    finally { setDownloading(false); }
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const r = await api.post("/reports/share-link", { member_id: memberId || null, start, end, expires_in_days: 7 });
      setLink(r.data);
      toast.success("Shareable link ready");
    } catch { toast.error("Could not create link"); }
    finally { setSharing(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const shareIt = async (url) => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Health Report", text: "Verda Health summary", url });
      } catch (err) {
        // navigator.share rejects on user cancel — not an error worth surfacing
        if (err?.name !== "AbortError") { toast.error("Could not share"); }
      }
    } else { copy(url); }
  };

  const memberName = members.find(m => m.id === memberId)?.name || "Self";

  return (
    <div className="space-y-6">
      <Header title="Share with Doctor"
        subtitle="Download a clean PDF or generate a private link your doctor can open." />

      {!isPro && (
        <Card className="p-5 rounded-2xl border-2 border-[#C76B58]/40 bg-gradient-to-r from-[#C76B58]/[0.05] to-[#4A6750]/[0.05] flex items-start gap-4" data-testid="pro-required-banner">
          <div className="w-10 h-10 rounded-xl bg-[#C76B58]/15 flex items-center justify-center flex-shrink-0">
            <Crown className="w-5 h-5 text-[#C76B58]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading text-lg font-semibold">Share with Doctor is a Pro feature</p>
            <p className="text-sm text-[#5C6D63] mt-0.5">
              Generate secure PDF exports and private 7-day links your doctor can review. Available with Verda Pro.
            </p>
          </div>
          <Button onClick={() => navigate("/subscription")} data-testid="pro-required-cta"
                  className="rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14] flex-shrink-0">
            <Lock className="w-4 h-4 mr-2" /> View Pro
          </Button>
        </Card>
      )}
      <Card className="p-6 rounded-2xl border border-[#E5E7E4]">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">Member</Label>
            <Select value={memberId} onValueChange={setMemberId}>
              <SelectTrigger data-testid="report-member" className="mt-1.5 rounded-xl bg-[#F9F8F6]"><SelectValue /></SelectTrigger>
              <SelectContent>{members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">From</Label>
            <Input type="date" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5 rounded-xl bg-[#F9F8F6]" />
          </div>
          <div>
            <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">To</Label>
            <Input type="date" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5 rounded-xl bg-[#F9F8F6]" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-btn"
              className="flex-1 rounded-xl bg-[#4A6750] hover:bg-[#3B5340] h-10">
              <Download className="w-4 h-4 mr-2" /> {downloading ? "Preparing…" : "Download PDF"}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-5 text-sm text-[#5C6D63]">
          <FileText className="w-4 h-4" />
          <span>Summary for <b className="text-[#1C2E24]">{memberName}</b> · {start} → {end}</span>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border border-[#E5E7E4]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E8ECE9] flex items-center justify-center">
            <Link2 className="w-5 h-5 text-[#4A6750]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-medium">Shareable doctor link</h3>
            <p className="text-sm text-[#5C6D63] mt-1">Generates a private, read-only link your doctor can view in their browser. Expires in 7 days. The PDF inside the link is also downloadable.</p>
            <Button onClick={createShareLink} disabled={sharing} data-testid="create-share-link-btn"
              className="mt-4 rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
              <Link2 className="w-4 h-4 mr-2" /> {sharing ? "Creating…" : "Create share link"}
            </Button>
          </div>
        </div>

        {link && (
          <div className="mt-6 p-4 rounded-xl bg-[#F4F6F2] border border-[#E5E7E4] space-y-3" data-testid="share-link-result">
            <div>
              <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">Web link (for doctor)</Label>
              <div className="flex gap-2 mt-1.5">
                <Input readOnly value={link.view_url} className="bg-white font-mono text-xs rounded-lg" data-testid="share-view-url" />
                <Button variant="outline" onClick={() => copy(link.view_url)} className="rounded-lg" data-testid="copy-view-url"><Copy className="w-4 h-4" /></Button>
                <Button variant="outline" onClick={() => shareIt(link.view_url)} className="rounded-lg" data-testid="share-view-url-btn"><Share2 className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">Direct PDF link</Label>
              <div className="flex gap-2 mt-1.5">
                <Input readOnly value={link.pdf_url} className="bg-white font-mono text-xs rounded-lg" data-testid="share-pdf-url" />
                <Button variant="outline" onClick={() => copy(link.pdf_url)} className="rounded-lg"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <p className="text-xs text-[#7D8F84] flex items-center gap-1.5">
              <CalIcon className="w-3 h-3" /> Expires {new Date(link.expires_at).toLocaleString()}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
